import React from 'react';
import classnames from 'classnames';
import { useTheme } from '../theme';
import {
  IconInformationSolid,
  IconCheckmarkCircle,
  IconWarningSign,
  IconExclamationCircle,
  IconCross,
} from '../icons';

const iconMap: Record<MessageVariant, null | React.ReactElement> = {
  default: null,
  informative: <IconInformationSolid />,
  positive: <IconCheckmarkCircle />,
  warning: <IconWarningSign />,
  error: <IconExclamationCircle />,
};

export const variants = ['default', 'informative', 'positive', 'warning', 'error'] as const;
export type MessageVariant = typeof variants[number];

export const sizes = ['small', 'medium'] as const;
export type MessageSize = typeof sizes[number];

export type MessageProps = React.HTMLAttributes<HTMLDivElement> & {
  cssBlockName: string;
  size?: MessageSize;
  variant?: MessageVariant;
  onClose?: () => void;
};

export type MessageImplementationProps = Omit<MessageProps, 'cssBlockName'>;

/**
 * This component should not be used directly.
 * It is used by the <InfoMessage> and <Banner> components.
 *
 * IMPORTANT: `cssBlockName` should be ommitted from props of the caller
 * and defined manually to match the CSS block name of the component.
 * See the <InfoMessage> component.
 */
export const Message = ({
  cssBlockName,
  className,
  size = 'medium',
  variant = 'default',
  onClose,
  children,
  ...props
}: MessageProps): React.ReactElement => {
  const theme = useTheme();
  const icon = iconMap[variant];
  const classNames = classnames(
    className,
    cssBlockName,
    `${cssBlockName}--size-${size}`,
    `${cssBlockName}--variant-${variant}`,
    {
      [`${cssBlockName}--has-icon`]: icon,
      [`${cssBlockName}--closeable`]: onClose,
    }
  );

  return (
    <div className={classNames} data-theme={theme} {...props}>
      {icon && <div className={`${cssBlockName}__icon`}>{icon}</div>}
      <div className={`${cssBlockName}__container`}>{children}</div>
      {onClose && (
        <span className={`${cssBlockName}__close-icon`} onClick={onClose}>
          <IconCross />
        </span>
      )}
    </div>
  );
};
